import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Btn, Input, Page, Pill, Select, Table, sGet, sPatch, sPost } from './staffShared';

function Categories({ canEdit }) {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ vertical: '', name: '' });
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => sGet('/categories?include_inactive=true').then(setRows).catch(e => toast.error(e.message)), []);
  useEffect(() => { load(); }, [load]);
  const verticals = Array.from(new Set(rows.map(r => r.vertical)));
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); setEdit(null); load(); } catch (e) { toast.error(e.message); } };
  return (
    <section className="space-y-3" data-testid="ref-categories">
      <h2 className="text-base md:text-lg font-semibold">Categories</h2>
      {canEdit && (
        <div className="rounded-lg border bg-white p-4 grid gap-3 md:grid-cols-3 items-end" style={{ borderColor: '#E5E3DC' }}>
          <Input label="Vertical (free text — existing ones suggested)" value={f.vertical} onChange={v => setF(s => ({ ...s, vertical: v }))} testId="cat-vertical" list="verticals" />
          <datalist id="verticals">{verticals.map(v => <option key={v} value={v} />)}</datalist>
          <Input label="Name" value={f.name} onChange={v => setF(s => ({ ...s, name: v }))} testId="cat-name" />
          <Btn testId="cat-add" disabled={!f.vertical || !f.name} onClick={() => act(() => sPost('/categories', f), 'Category added').then(() => setF({ vertical: '', name: '' }))}>Add category</Btn>
        </div>
      )}
      <Table testId="categories-table" cols={['Vertical', 'Name', 'Tenants', 'Status', '']} rows={rows} render={c => (
        <tr key={c.id} className="border-t" style={{ borderColor: '#F3F1EA', opacity: c.is_active ? 1 : 0.6 }} data-testid={`cat-row-${c.id}`}>
          <td className="px-4 py-2">{c.vertical}</td>
          <td className="px-4 py-2 font-semibold">{edit?.id === c.id ? <input data-testid={`cat-edit-name-${c.id}`} className="h-8 rounded border px-2" style={{ borderColor: '#D9D6CD' }} value={edit.name} onChange={e => setEdit({ ...edit, name: e.target.value })} /> : c.name}</td>
          <td className="px-4 py-2">{c.tenant_count}</td>
          <td className="px-4 py-2">{c.is_active ? <Pill>active</Pill> : <Pill tone="muted">inactive</Pill>}</td>
          <td className="px-4 py-2 text-right whitespace-nowrap text-xs">
            {canEdit && (edit?.id === c.id
              ? <><button data-testid={`cat-save-${c.id}`} className="underline mr-3" style={{ color: '#0B6B55' }} onClick={() => act(() => sPatch(`/categories/${c.id}`, { name: edit.name }), 'Renamed')}>Save</button><button className="underline" onClick={() => setEdit(null)}>Cancel</button></>
              : <><button data-testid={`cat-rename-${c.id}`} className="underline mr-3" style={{ color: '#0B6B55' }} onClick={() => setEdit({ id: c.id, name: c.name })}>Rename</button>
                <button data-testid={`cat-toggle-${c.id}`} className="underline" style={{ color: '#6b7069' }} onClick={() => act(() => sPost(`/categories/${c.id}/${c.is_active ? 'deactivate' : 'reactivate'}`, {}), c.is_active ? 'Deactivated' : 'Reactivated')}>{c.is_active ? 'Deactivate' : 'Reactivate'}</button></>)}
          </td>
        </tr>
      )} />
    </section>
  );
}

function StatesAndLocations({ canEdit }) {
  const [states, setStates] = useState([]);
  const [locs, setLocs] = useState([]);
  const [f, setF] = useState({ state_code: '', name: '' });
  const load = useCallback(() => Promise.all([sGet('/states'), sGet('/locations')]).then(([s, l]) => { setStates(s); setLocs(l); }).catch(e => toast.error(e.message)), []);
  useEffect(() => { load(); }, [load]);
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(e.message); } };
  return (
    <section className="space-y-3" data-testid="ref-locations">
      <h2 className="text-base md:text-lg font-semibold">States and locations</h2>
      {canEdit && (
        <div className="rounded-lg border bg-white p-4 grid gap-3 md:grid-cols-3 items-end" style={{ borderColor: '#E5E3DC' }}>
          <Select label="State" value={f.state_code} onChange={v => setF(s => ({ ...s, state_code: v }))} testId="loc-state" options={states.filter(s => s.is_active).map(s => [s.code, `${s.code} · ${s.name}`])} />
          <Input label="Location name" value={f.name} onChange={v => setF(s => ({ ...s, name: v }))} testId="loc-name" />
          <Btn testId="loc-add" disabled={!f.state_code || !f.name} onClick={() => act(() => sPost('/locations', f), 'Location added').then(() => setF(s => ({ ...s, name: '' })))}>Add location</Btn>
        </div>
      )}
      <Table testId="locations-table" cols={['State', 'Location', 'Status', '']} rows={locs} render={l => (
        <tr key={l.id} className="border-t" style={{ borderColor: '#F3F1EA' }} data-testid={`loc-row-${l.id}`}>
          <td className="px-4 py-2">{l.state_code} · {states.find(s => s.code === l.state_code)?.name}</td>
          <td className="px-4 py-2 font-semibold">{l.name}</td>
          <td className="px-4 py-2">{l.is_active ? <Pill>active</Pill> : <Pill tone="muted">inactive</Pill>}</td>
          <td className="px-4 py-2 text-right text-xs">{canEdit && <button data-testid={`loc-toggle-${l.id}`} className="underline" style={{ color: '#6b7069' }} onClick={() => act(() => sPatch(`/locations/${l.id}`, { is_active: !l.is_active }), 'Updated')}>{l.is_active ? 'Deactivate' : 'Reactivate'}</button>}</td>
        </tr>
      )} />
      <details><summary className="text-sm cursor-pointer" style={{ color: '#6b7069' }}>All {states.length} states / UTs</summary>
        <div className="flex flex-wrap gap-2 mt-2" data-testid="states-list">{states.map(s => <span key={s.code} className="text-xs px-2 py-1 rounded border" style={{ borderColor: '#D9D6CD', opacity: s.is_active ? 1 : 0.5 }}>
          {s.code} {s.name}{canEdit && <button data-testid={`state-toggle-${s.code}`} className="ml-2 underline" onClick={() => act(() => sPatch(`/states/${s.code}`, { is_active: !s.is_active }), 'Updated')}>{s.is_active ? 'off' : 'on'}</button>}</span>)}</div>
      </details>
    </section>
  );
}

export default function SettingsRefPage() {
  const [me, setMe] = useState(null);
  useEffect(() => { sGet('/me').then(setMe).catch(() => setMe({})); }, []);
  const canEdit = me?.role_code === 'ADMIN';
  return (
    <Page title="Categories · States · Locations" sub={canEdit ? 'Reference data, not code. Deactivating a category in use is refused with the tenant count.' : 'Read-only for your role.'} testId="settings-ref-page">
      <Categories canEdit={canEdit} />
      <StatesAndLocations canEdit={canEdit} />
    </Page>
  );
}
